// LiveMode.tsx
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUserMerchants,
  selectMerchants,
  selectMerchantLoading,
  selectMerchantError,
  Merchant,
} from "../../../redux/merchantSlice";
import type { AppDispatch, RootState } from "../../../redux/store";

export default function LiveMode() {
  const dispatch = useDispatch<AppDispatch>();

  // 1. Get the authenticated user from Redux
  const user = useSelector((state: RootState) => state.auth.user);
  
  const merchants = useSelector(selectMerchants);
  const loading = useSelector(selectMerchantLoading);
  const error = useSelector(selectMerchantError);

  useEffect(() => {
    if (user?.userId) {
      dispatch(fetchUserMerchants(user.userId));
    }
  }, [dispatch, user?.userId]);

  if (loading) return <div style={{ padding: 20 }}>Loading live environment…</div>;
  if (error)   return <div style={{ padding: 20, color: "#C8303A" }}>{error}</div>;
  if (!user)    return <div style={{ padding: 20 }}>Please sign in to access live keys.</div>;

  return (
    <div style={{ padding: 20, fontFamily: "'Libre Franklin', sans-serif" }}>
      <h2 style={{ marginBottom: 16, color: "#1A1714" }}>Live Mode API Keys</h2>

      {merchants.length === 0 && <p>No organisations found.</p>}

      {merchants.map((m: Merchant) => {
        // 2. Access Control: Must be KYC verified AND Live Enabled
        const isLiveReady = m.keys?.kycVerified === true && m.liveEnabled === true;

        return (
          <div key={m.merchantId} style={{ marginBottom: 24, border: "1px solid #D4CFC8", padding: 16, borderRadius: 8, background: isLiveReady ? "#FFFFFF" : "#F9F9F9" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <h3 style={{ marginBottom: 4 }}>{m.businessName}</h3>
              <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: isLiveReady ? "#4A7C59" : "#D4CFC8", color: "#FFF" }}>
                {isLiveReady ? "LIVE" : "RESTRICTED"}
              </span>
            </div>
            
            <p style={{ fontSize: 12, color: "#666", marginBottom: 12 }}>Merchant ID: {m.merchantId}</p>

            {isLiveReady ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <div>
                  <strong style={{ fontSize: 13 }}>Live Publishable Key:</strong><br/>
                  <code style={{ background: "#F5F0EB", padding: "2px 6px", display: "block", marginTop: 4 }}>
                    {m.keys?.pk_live || "Not Generated"}
                  </code>
                </div>
                
                <div>
                  <strong style={{ fontSize: 13 }}>Live Secret Key:</strong><br/>
                  <code style={{ background: "#F5F0EB", padding: "2px 6px", color: "#9B958D", display: "block", marginTop: 4 }}>
                    {m.keys?.sk_live_hash ? "sk_live_••••••••••••••••••••" : "Not Generated"}
                  </code>
                </div>
              </div>
            ) : (
              <div style={{ marginTop: 12, padding: 12, background: "#FFF5F5", border: "1px solid #FFDCDC", borderRadius: 4 }}>
                <p style={{ fontSize: 13, color: "#C8303A", margin: 0, fontWeight: 600 }}>
                  {!m.keys?.kycVerified ? "⚠️ KYC Verification Required" : "⚠️ Live Mode Not Enabled"}
                </p>
                <p style={{ fontSize: 11, color: "#6B6560", marginTop: 4 }}>
                  Complete your business profile and identity verification to access live production keys.
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
